import React, { useState, forwardRef, useMemo, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import Divider from 'src/components/ui/Divider/Divider';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Checkbox from '../../ui/Checkbox/Checkbox';
import Input from '../../ui/Input/Input';
import Select, { Option } from '../../ui/Select/Select';
import { FormData, ReturnsTableProps, getSchemaForSelected } from './ProductsSearchTable';
import { useReturnReasonsQuery } from '../../../services/returns';
import { getProductImageUrl } from '../../../services/products';

const ProductsSearchTableMobile = forwardRef<{ submitForm: () => void }, ReturnsTableProps>(
  ({ data, onSelectionChange, readonly = false }, ref) => {
    const { t } = useTranslation();
    const [selectedReasons, setSelectedReasons] = useState<Record<string, Option | null>>({});
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const { data: reasons } = useReturnReasonsQuery();
    const reasonOptions: Option[] =
      reasons?.map(reason => ({
        label: reason.name,
        value: reason.id.toString(),
      })) || [];

    const dynamicSchema = useMemo(() => getSchemaForSelected(selectedProductIds, t), [selectedProductIds, t]);

    const {
      control,
      handleSubmit,
      formState: { errors, isValid },
    } = useForm<FormData>({
      resolver: zodResolver(dynamicSchema),
      defaultValues: {
        returnData: data.reduce(
          (acc: { [x: string]: { return_quantity: number; reason: string } }, row: { product_id: string | number }) => {
            acc[row.product_id] = { return_quantity: 0, reason: '' };
            return acc;
          },
          {},
        ),
      },
    });

    const updateReturnReason = (id: string, reason: Option | null) => {
      setSelectedReasons(prev => ({ ...prev, [id]: reason }));
    };

    const onSubmit = (formData: FormData) => {
      if (isValid) {
        const selectedReturnData = Object.entries(formData.returnData)
          .filter(([productId]) => selectedProductIds.includes(productId))
          .map(([productId, { return_quantity, reason_id }]) => ({
            product_id: Number(productId),
            quantity: return_quantity,
            reason_id,
          }));

        onSelectionChange(selectedReturnData);
      }
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        handleSubmit(onSubmit as any)();
      },
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit as any)}>
        <div className="flex flex-col w-full">
          <span className="typo-text-16--bold">{t('returns.your_order')}</span>
          {data.map((product: any) => (
            <div key={product.product_id} className="flex flex-col gap-[12px] mt-[12px]">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-[12px]">
                  <Checkbox
                    checked={selectedProductIds.includes(String(product.product_id))}
                    disabled={readonly || product.quantity === 0}
                    onChange={() => {
                      setSelectedProductIds(prev =>
                        prev.includes(String(product.product_id))
                          ? prev.filter(id => id !== String(product.product_id))
                          : [...prev, String(product.product_id)],
                      );
                    }}
                  />
                  <img
                    className="w-[36px] h-[36px] rounded-xl border-subtle"
                    src={product?.product_id ? getProductImageUrl(Number(product.product_id)) : '/testImage.png'}
                    alt="img"
                  />
                  <span className="typo-text-12--bold">{product.product_name}</span>
                </div>
                <span className="typo-text-12 text-faded">{product.quantity}x</span>
              </div>

              <div className="flex flex-col gap-[16px] w-full">
                <div className="flex items-center justify-between w-full">
                  <span className="typo-text-14--bold text-faded">{t('product_packaging.quantity')}</span>
                  <div className="flex flex-col max-w-[240px] w-full">
                    <Controller
                      name={`returnData.${product.product_id}.return_quantity`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          className="max-w-[215px]"
                          placeholder="0"
                          min={0}
                          max={product.quantity}
                          disabled={
                            readonly ||
                            product.quantity === 0 ||
                            !selectedProductIds.includes(String(product.product_id))
                          }
                          onChange={e => {
                            field.onChange(Number(e.target.value));
                          }}
                        />
                      )}
                    />
                    <span className="typo-text--12 status-error-full">
                      {errors?.returnData?.[product.product_id]?.return_quantity?.message}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between w-full">
                  <span className="typo-text-14--bold text-faded">{t('returns.reason')}</span>
                  <div className="flex flex-col max-w-[240px] w-full">
                    <Controller
                      name={`returnData.${product.product_id}.reason`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <Select
                            {...field}
                            className="max-w-[215px]"
                            options={reasonOptions}
                            defaultValue={
                              selectedReasons[product.product_id] ||
                              reasonOptions?.find(item => item.value === product.reason)
                            }
                            placeholder={t('general.select')}
                            disabled={
                              readonly ||
                              product.quantity === 0 ||
                              !selectedProductIds.includes(String(product.product_id))
                            }
                            onChange={selected => {
                              updateReturnReason(product.product_id, selected);
                              field.onChange(selected?.value);
                            }}
                          />
                        );
                      }}
                    />
                    <span className="typo-text--12 status-error-full">
                      {errors?.returnData?.[product.product_id]?.reason_id?.message}
                    </span>
                  </div>
                </div>
              </div>
              <Divider className="my-[12px]" />
            </div>
          ))}
        </div>
      </form>
    );
  },
);

export default ProductsSearchTableMobile;
